import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

const TradingAccounts = ({ 
  onCreateTradingAccount, 
  onGetTradingAccountList, 
  onGetTradingAccountListAll, 
  onDeleteTradingAccount, 
  onGetUsersList,
  currentUser 
}) => {
  const [tradingAccounts, setTradingAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterBroker, setFilterBroker] = useState('all');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [viewMode, setViewMode] = useState('user'); // kept for compatibility; no longer affects API used
  const [selectedUserId, setSelectedUserId] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [strategies, setStrategies] = useState([]);

  const [formData, setFormData] = useState({
    accountId: '',
    userId: '', // Always start with empty selection
    accLoginName: '',
    loginPlatform: 1,
    loginServer: '',
    accLoginPassword: '',
    initialDeposit: 1000,
    brokerName: 1,
    remarks: 0,
    shortDescription: '',
    holdOnWeekend: false,
    maximumDrawdown: 10,
    binanceFuturesApiKey: '',
    binanceFuturesSecretKey: '',
    allowDataFetch: true,
    allowTrading: true,
    strategyId: 0,
    initialSizeInUsdt: 10,
    leverage: 20,
    firstTakeProfitPer: 3,
    secondTakeProfitPer: 5,
    stopLoss: 2,
    market: 1 // Binance
  });

  // Load trading accounts and users on component mount
  useEffect(() => {
    // Initialize selectedUserId to current user if empty
    if (!selectedUserId && currentUser?.userId) {
      setSelectedUserId(currentUser.userId);
    }

    loadTradingAccounts();
    loadUsers();
  }, [viewMode, currentUser, selectedUserId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getStrategies();
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) setStrategies(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setStrategies([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadTradingAccounts = async () => {
    setLoading(true);
    try {
      const userId = selectedUserId || currentUser?.userId;
      console.log('Loading trading accounts for userId:', userId);

      const result = await onGetTradingAccountList({ userId });

      if (result.success) {
        setTradingAccounts(result.data);
      } else {
        console.error('Failed to load trading accounts:', result.error);
      }
    } catch (error) {
      console.error('Error loading trading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      console.log('Loading users...');
      const result = await onGetUsersList();
      console.log('Users result:', result);
      if (result.success) {
        setUsers(result.data);
        console.log('Users loaded successfully:', result.data.length);
      } else {
        console.error('Failed to load users:', result.error);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleUserSelectionChange = (userId) => {
    console.log('User selection changed to:', userId);
    setSelectedUserId(userId);
    // Trading accounts will be reloaded automatically due to useEffect dependency
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const intFields = new Set([
      'accountId',
      'userId',
      'loginPlatform',
      'brokerName',
      'remarks',
      'strategyId',
      'leverage',
      'market',
    ]);
    const floatFields = new Set([
      'initialDeposit',
      'maximumDrawdown',
      'initialSizeInUsdt',
      'firstTakeProfitPer',
      'secondTakeProfitPer',
      'stopLoss',
    ]);

    setFormData((prev) => {
      let nextVal;
      if (type === 'checkbox') {
        nextVal = checked;
      } else if (intFields.has(name)) {
        nextVal = value === '' ? '' : Number.parseInt(value, 10) || 0;
      } else if (floatFields.has(name)) {
        nextVal = value === '' ? '' : Number.parseFloat(value) || 0;
      } else {
        nextVal = value;
      }
      return { ...prev, [name]: nextVal };
    });
  };

  const resetForm = () => {
    setFormData({
      accountId: '',
      userId: '', // Always start with empty selection
      accLoginName: '',
      loginPlatform: 1,
      loginServer: '',
      accLoginPassword: '',
      initialDeposit: 1000,
      brokerName: 1,
      remarks: 0,
      shortDescription: '',
      holdOnWeekend: false,
      maximumDrawdown: 10,
      binanceFuturesApiKey: '',
      binanceFuturesSecretKey: '',
      allowDataFetch: true,
      allowTrading: true,
      strategyId: 0,
      initialSizeInUsdt: 10,
      leverage: 20,
      firstTakeProfitPer: 3,
      secondTakeProfitPer: 5,
      stopLoss: 2,
      market: 1
    });
    setEditingAccount(null);
    setShowCreateForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await onCreateTradingAccount(formData);

      if (result.success) {
        resetForm();
        loadTradingAccounts(); // Reload accounts list
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error saving trading account:', error);
      alert('Failed to save trading account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGetAccountInfo = async () => {
    try {
      const { binanceFuturesApiKey, binanceFuturesSecretKey } = formData;
      if (!binanceFuturesApiKey || !binanceFuturesSecretKey) {
        alert('Please enter API Key and Secret Key first');
        return;
      }
      setLoading(true);
      const info = await apiService.getAccountInfo({ binanceFuturesApiKey, binanceFuturesSecretKey });
      if (info && info.errorCode === 200) {
        const rounded = typeof info.initialDeposit === 'number'
          ? Math.round(info.initialDeposit * 100) / 100
          : info.initialDeposit;
        setFormData(prev => ({
          ...prev,
          accLoginName: info.accLoginName || prev.accLoginName,
          initialDeposit: typeof rounded === 'number' ? rounded : prev.initialDeposit
        }));
      } else {
        alert(info?.errorMessage || 'Failed to fetch account info');
      }
    } catch (e) {
      alert('Failed to fetch account info');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setShowCreateForm(true);
    setFormData({
      accountId: account.accountId,
      userId: account.userId,
      accLoginName: account.accLoginName || '',
      loginPlatform: account.loginPlatform || 1,
      loginServer: account.loginServer || '',
      accLoginPassword: '',
      initialDeposit: account.initialDeposit ?? 1000,
      brokerName: account.brokerName || 1,
      remarks: account.remarks ?? 0,
      shortDescription: account.shortDescription || '',
      holdOnWeekend: account.holdOnWeekend || false,
      maximumDrawdown: account.maximumDrawdown ?? 10,
      binanceFuturesApiKey: account.binanceFuturesApiKey || '',
      binanceFuturesSecretKey: account.binanceFuturesSecretKey || '',
      allowDataFetch: account.allowDataFetch != null ? Boolean(account.allowDataFetch) : true,
      allowTrading: account.allowTrading != null ? Boolean(account.allowTrading) : true,
      strategyId: account.strategyId ?? 0,
      initialSizeInUsdt: account.initialSizeInUsdt ?? 10,
      leverage: account.leverage ?? 20,
      firstTakeProfitPer: account.firstTakeProfitPer ?? 3,
      secondTakeProfitPer: account.secondTakeProfitPer ?? 5,
      stopLoss: account.stopLoss ?? 2,
      market: account.market ?? 1
    });
  };

  const handleDelete = (account) => {
    setAccountToDelete(account);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!accountToDelete) return;
    
    setLoading(true);
    try {
      const result = await onDeleteTradingAccount({
        userId: accountToDelete.userId,
        accLoginName: accountToDelete.accLoginName
      });

      if (result.success) {
        loadTradingAccounts(); // Reload accounts list
        setShowDeleteConfirm(false);
        setAccountToDelete(null);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting trading account:', error);
      alert('Failed to delete trading account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedAccounts.length === 0) {
      alert('Please select accounts first');
      return;
    }

    setLoading(true);
    try {
      for (const accountId of selectedAccounts) {
        const account = tradingAccounts.find(a => a.userId === accountId);
        if (account) {
          await onDeleteTradingAccount({
            userId: account.userId,
            accLoginName: account.accLoginName
          });
        }
      }
      
      setSelectedAccounts([]);
      loadTradingAccounts(); // Reload accounts list
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert('Failed to perform bulk action. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAccount = (accountId) => {
    setSelectedAccounts(prev => 
      prev.includes(accountId) 
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAccounts.length === filteredAccounts.length) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(filteredAccounts.map(account => account.userId));
    }
  };

  const getPlatformName = (platform) => {
    switch (platform) {
      case 1: return 'MT5';
      case 2: return 'Binance';
      case 3: return 'CTrader';
      case 4: return 'ByBit';
      default: return 'Unknown';
    }
  };

  const getBrokerName = (broker) => {
    switch (broker) {
      case 1: return 'Binance';
      case 2: return 'MetaTrader';
      case 3: return 'Other';
      default: return 'Unknown';
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.userId === userId);
    return user ? `${user.userFirstName} ${user.userLastName}` : 'Unknown User';
  };

  const activeUsers = users.filter(u => Number(u?.status) === 1);

  // Filter accounts based on search term and filters
  const filteredAccounts = tradingAccounts.filter(account => {
    const matchesSearch = searchTerm === '' || 
      account.accLoginName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPlatform = filterPlatform === 'all' || account.loginPlatform === parseInt(filterPlatform);
    const matchesBroker = filterBroker === 'all' || account.brokerName === parseInt(filterBroker);
    
    return matchesSearch && matchesPlatform && matchesBroker;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl sm:text-2xl font-semibold text-white">Trading Accounts</h3>
          <p className="text-dark-400 text-sm mt-1">Manage your trading accounts</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {/* User Selection */}
          <div className="flex items-center gap-2">
            <label className="text-dark-300 text-sm font-medium">User:</label>
            <select
              value={selectedUserId}
              onChange={(e) => handleUserSelectionChange(e.target.value)}
              className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm min-w-[200px]"
            >
              <option value="">Select a user...</option>
              {(() => {
                const selected = users.find(u => String(u.userId) === String(selectedUserId));
                const includeSelected = selected && Number(selected?.status) !== 1;
                const optionUsers = includeSelected
                  ? [...activeUsers, selected].filter(
                      (u, idx, arr) => arr.findIndex(x => String(x.userId) === String(u.userId)) === idx
                    )
                  : activeUsers;
                return optionUsers.map((user) => (
                  <option
                    key={user.userId}
                    value={user.userId}
                    disabled={Number(user?.status) !== 1}
                  >
                    {user.userFirstName} {user.userLastName} ({user.eMailId})
                    {Number(user?.status) !== 1 ? ' (Suspended)' : ''}
                  </option>
                ));
              })()}
            </select>
            {selectedUserId && (
              <span className="text-xs text-dark-400">
                Showing accounts for: {getUserName(selectedUserId)}
              </span>
            )}
          </div>
          {/* Removed view mode toggles; accounts are loaded for selected user */}
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-primary-600 text-white px-4 sm:px-5 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg touch-manipulation text-sm sm:text-base w-full sm:w-auto"
          >
            + Add Trading Account
          </button>
          {selectedAccounts.length > 0 && (
            <button
              onClick={() => handleBulkAction('delete')}
              className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-red-700 transition-colors"
            >
              Delete ({selectedAccounts.length})
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-dark-300 font-medium mb-2 text-sm">Search Accounts</label>
            <input
              type="text"
              placeholder="Search by login name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-dark-300 font-medium mb-2 text-sm">Filter by Platform</label>
            <select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value)}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm"
            >
              <option value="all">All Platforms</option>
              <option value="1">MT5</option>
              <option value="2">Binance</option>
              <option value="3">CTrader</option>
              <option value="4">ByBit</option>
            </select>
          </div>
          <div>
            <label className="block text-dark-300 font-medium mb-2 text-sm">Filter by Broker</label>
            <select
              value={filterBroker}
              onChange={(e) => setFilterBroker(e.target.value)}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm"
            >
              <option value="all">All Brokers</option>
              <option value="1">Binance</option>
              <option value="2">MetaTrader</option>
              <option value="3">Other</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterPlatform('all');
                setFilterBroker('all');
              }}
              className="w-full bg-dark-700 text-dark-200 px-3 py-2 rounded-lg text-sm font-medium hover:bg-dark-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
          <div className="flex justify-between items-center mb-4 sm:mb-5">
            <h4 className="text-lg sm:text-xl font-semibold text-white">
              {editingAccount ? 'Edit Trading Account' : 'Add New Trading Account'}
            </h4>
            <button
              onClick={resetForm}
              className="text-dark-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* User Selection - Show for all users, but make it required for admins */}
            <div>
              <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                Select User *
              </label>
              <select
                name="userId"
                value={formData.userId}
                onChange={handleInputChange}
                required
                disabled={!!editingAccount}
                className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select a user...</option>
                {(() => {
                  const selected = users.find(u => String(u.userId) === String(formData.userId));
                  const includeSelected = !!editingAccount && selected && Number(selected?.status) !== 1;
                  const optionUsers = includeSelected
                    ? [...activeUsers, selected].filter(
                        (u, idx, arr) => arr.findIndex(x => String(x.userId) === String(u.userId)) === idx
                      )
                    : activeUsers;
                  return optionUsers.map((user) => (
                    <option
                      key={user.userId}
                      value={user.userId}
                      disabled={Number(user?.status) !== 1}
                    >
                      {user.userFirstName} {user.userLastName} ({user.eMailId})
                      {Number(user?.status) !== 1 ? ' (Suspended)' : ''}
                    </option>
                  ));
                })()}
              </select>
              {users.length === 0 && (
                <p className="text-yellow-400 text-sm mt-1">Loading users...</p>
              )}
              <p className="text-dark-400 text-xs mt-1">
                Current user role: {currentUser?.userRole} | Users loaded: {users.length}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Account Login Name *
                </label>
                <input
                  type="text"
                  name="accLoginName"
                  value={formData.accLoginName}
                  required
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Login Platform *
                </label>
                <select
                  name="loginPlatform"
                  value={formData.loginPlatform}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                >
                  <option value={1}>MT5</option>
                  <option value={2}>Binance</option>
                  <option value={3}>CTrader</option>
                  <option value={4}>ByBit</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Login Server
                </label>
                <input
                  type="text"
                  name="loginServer"
                  value={formData.loginServer}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Account Password
                </label>
                <input
                  type="password"
                  name="accLoginPassword"
                  value={formData.accLoginPassword}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Initial Deposit *
                </label>
                <input
                  type="number"
                  name="initialDeposit"
                  value={formData.initialDeposit}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Broker Name *
                </label>
                <select
                  name="brokerName"
                  value={formData.brokerName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                >
                  <option value={1}>Binance</option>
                  <option value={2}>MetaTrader</option>
                  <option value={3}>Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                Short Description
              </label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Remarks (numeric)
                </label>
                <input
                  type="number"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  step="1"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center text-dark-300 font-medium text-sm sm:text-base">
                  <input
                    type="checkbox"
                    name="allowDataFetch"
                    checked={!!formData.allowDataFetch}
                    onChange={handleInputChange}
                    className="rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500 mr-2"
                  />
                  Allow Data Fetch
                </label>
                <label className="flex items-center text-dark-300 font-medium text-sm sm:text-base">
                  <input
                    type="checkbox"
                    name="allowTrading"
                    checked={!!formData.allowTrading}
                    onChange={handleInputChange}
                    className="rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500 mr-2"
                  />
                  Allow Trading
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Strategy
                </label>
                <select
                  name="strategyId"
                  value={formData.strategyId}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                >
                  <option value={0}>Default (0)</option>
                  {strategies.map((s) => {
                    const id = Number(s?.strategyId ?? s?.strategyNameId ?? s?.id ?? 0) || 0;
                    if (!id) return null;
                    const label = s?.strategyName || s?.name || `Strategy ${id}`;
                    return (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    );
                  })}
                </select>
                {strategies.length === 0 && (
                  <p className="text-dark-400 text-xs mt-1">
                    Strategies not loaded (will still save with strategyId {formData.strategyId}).
                  </p>
                )}
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Market
                </label>
                <select
                  name="market"
                  value={formData.market}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                >
                  <option value={1}>Binance</option>
                  <option value={2}>FXCM</option>
                  <option value={3}>MT5</option>
                  <option value={4}>cTrader</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Initial Size (USDT)
                </label>
                <input
                  type="number"
                  name="initialSizeInUsdt"
                  value={formData.initialSizeInUsdt}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Leverage
                </label>
                <input
                  type="number"
                  name="leverage"
                  value={formData.leverage}
                  onChange={handleInputChange}
                  min="1"
                  step="1"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  First Take Profit (%)
                </label>
                <input
                  type="number"
                  name="firstTakeProfitPer"
                  value={formData.firstTakeProfitPer}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Second Take Profit (%)
                </label>
                <input
                  type="number"
                  name="secondTakeProfitPer"
                  value={formData.secondTakeProfitPer}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Stop Loss (%)
                </label>
                <input
                  type="number"
                  name="stopLoss"
                  value={formData.stopLoss}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Maximum Drawdown (%)
                </label>
                <input
                  type="number"
                  name="maximumDrawdown"
                  value={formData.maximumDrawdown}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="holdOnWeekend"
                  checked={formData.holdOnWeekend}
                  onChange={handleInputChange}
                  className="rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500 mr-2"
                />
                <label className="text-dark-300 font-medium text-sm sm:text-base">
                  Hold on Weekend
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Binance API Key
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    name="binanceFuturesApiKey"
                    value={formData.binanceFuturesApiKey}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-300 hover:text-white"
                    aria-label="Toggle API key visibility"
                    title={showApiKey ? 'Hide API Key' : 'Show API Key'}
                  >
                    {showApiKey ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Binance Secret Key
                </label>
                <div className="relative">
                  <input
                    type={showSecretKey ? 'text' : 'password'}
                    name="binanceFuturesSecretKey"
                    value={formData.binanceFuturesSecretKey}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecretKey(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-300 hover:text-white"
                    aria-label="Toggle Secret key visibility"
                    title={showSecretKey ? 'Hide Secret Key' : 'Show Secret Key'}
                  >
                    {showSecretKey ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-primary-600 text-white px-4 sm:px-5 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg touch-manipulation text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (editingAccount ? 'Updating...' : 'Creating...') : (editingAccount ? 'Update Account' : 'Create Account')}
              </button>
              <button
                type="button"
                onClick={handleGetAccountInfo}
                disabled={loading}
                className="bg-dark-700 text-dark-200 px-4 sm:px-5 py-3 rounded-lg font-medium hover:bg-dark-600 transition-colors shadow-md hover:shadow-lg touch-manipulation text-sm sm:text-base"
              >
                Get Info
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-dark-700 text-dark-200 px-4 sm:px-5 py-3 rounded-lg font-medium hover:bg-dark-600 transition-colors shadow-md hover:shadow-lg touch-manipulation text-sm sm:text-base"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Trading Accounts List */}
      <div className="bg-dark-800 border border-dark-700 rounded-xl overflow-hidden shadow-lg">
        <div className="p-4 sm:p-6 border-b border-dark-700">
          <h4 className="text-lg sm:text-xl font-semibold text-white">Trading Accounts List</h4>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-dark-400">Loading trading accounts...</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-dark-400">No trading accounts found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-dark-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">
                    <input
                      type="checkbox"
                      checked={selectedAccounts.length === filteredAccounts.length && filteredAccounts.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">User</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Login Name</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Platform</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Broker</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Initial Deposit</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Max Drawdown</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Description</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => (
                  <tr key={`${account.userId}-${account.accLoginName}`} className="border-b border-dark-700 hover:bg-dark-700/50 transition-colors">
                    <td className="px-4 py-3 text-sm sm:text-base">
                      <input
                        type="checkbox"
                        checked={selectedAccounts.includes(account.userId)}
                        onChange={() => handleSelectAccount(account.userId)}
                        className="rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-blue-400 font-medium">
                      {getUserName(account.userId)}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-white font-medium">
                      {account.accLoginName}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-white">
                      {getPlatformName(account.loginPlatform)}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-white">
                      {getBrokerName(account.brokerName)}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-green-400">
                      ${account.initialDeposit?.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-yellow-400">
                      {account.maximumDrawdown}%
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-dark-300 truncate max-w-xs">
                      {account.shortDescription || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(account)}
                          className="text-primary-400 hover:text-primary-300 transition-colors"
                          title="Edit Account"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(account)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                          title="Delete Account"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredAccounts.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-dark-400">No trading accounts found matching your criteria</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 border border-dark-700 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-4">Confirm Delete</h3>
            <p className="text-dark-300 mb-6">
              Are you sure you want to delete trading account "{accountToDelete?.accLoginName}"? 
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                disabled={loading}
                className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete Account'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setAccountToDelete(null);
                }}
                className="bg-dark-700 text-dark-200 px-4 py-2 rounded-lg font-medium hover:bg-dark-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingAccounts;
